import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef } from 'react';
import type { TextareaProps } from './Textarea.types';
import './Textarea.scss';

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, maxLength, maxHeight = 280, id, required, className, value, onInput, ...rest },
  ref,
) {
  const autoId = useId();
  const taId = id || `textarea-${autoId}`;
  const innerRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement, []);

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = next + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [maxHeight]);

  useEffect(() => {
    resize();
  }, [resize, value]);

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    resize();
    onInput?.(e);
  };

  const describedBy = error ? `${taId}-error` : hint ? `${taId}-hint` : undefined;
  const length = typeof value === 'string' ? value.length : 0;

  return (
    <div className={['textarea', className || ''].filter(Boolean).join(' ')}>
      {label ? (
        <label className="textarea__label" htmlFor={taId}>
          {label}
          {required ? <span className="textarea__required" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      <textarea
        ref={innerRef}
        id={taId}
        required={required}
        maxLength={maxLength}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy}
        className={['textarea__field', error ? 'textarea__field--error' : ''].filter(Boolean).join(' ')}
        value={value}
        onInput={handleInput}
        {...rest}
      />
      <div className="textarea__meta">
        {error ? (
          <span id={`${taId}-error`} className="textarea__error" role="alert">{error}</span>
        ) : hint ? (
          <span id={`${taId}-hint`} className="textarea__hint">{hint}</span>
        ) : <span />}
        {maxLength ? <span className="textarea__counter">{length}/{maxLength}</span> : null}
      </div>
    </div>
  );
});

export default Textarea;
